import type { State } from '../configureStore'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import { Networks as ActionTypes } from '../actionTypes'
import {
  defaultState,
  InstanceRole,
  NetworkSummaryItem,
  NetworkTotals
} from '../reducers/networks/summary/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import { identity, map, pickBy } from 'lodash'
import {
  getConfigFromType,
  NetworkTileConfiguration,
  NetworkTiles
} from '@src/modules/networks/configurations/details.configurations/tiles.configurations'
import {
  CounterFromTab,
  getTabs,
  NetworkTabs,
  tabsConfig
} from '@src/modules/networks/configurations/details.configurations/tabs.configurations'
import { formatSGRulesList, getNetworkTotals } from '@src/utils/networks.utils'
import {
  getRegionFromUri,
  getTypeFromUri,
  parseCamURI
} from '@src/utils/url.utils'

import type {
  InstanceInfo,
  RoutesInfo,
  SGInfo,
  SGRulesInfo,
  SubnetInfo
} from '../reducers/networks/networking'
import {
  getHumanNameByType,
  HumanNamesResponse,
  InfoDetailsResult,
  parseActionsFromObject,
  parseInfoDetails,
  ResourceInfo
} from '@src/utils/resource.utils'
import {
  ResourceTypeEnum,
  RouteInfo
} from '@src/endpoints/services/commonHdr.service'
import {
  DataType,
  GraphQueryMgr,
  InstanceRoleDetails,
  NetworkDetailInfo,
  NetworkQueryResp,
  NetworkServiceQueryReq,
  NetworkSummaryInfo,
  NetworkTableTab,
  NetworkTileType
} from '@src/endpoints/services/graphQueryMgr.service'
import mapToDictionary from '@src/utils/dictionary'
import type { Graph } from '@src/components/vis.js.network.vis/models'
import { getAccountNameById } from '@src/utils/accounts'
import type { AccountInfo } from '@src/endpoints/services/base.service'
import type { Tenant } from '../reducers/auth'
import { getInfo } from '../reducers/objects/helpers/object.info.mapper'
import type { ResourceTypeDict } from '../reducers/shell'
import { mapDataToGraph } from '@src/utils/graph.helpers'

interface BaseOptions {
  uri: string
  type?: ResourceTypeEnum
  accountId?: string
}

// Action Creators
export type ActionTypes =
  | getStatSuccessAction
  | getNetworkingAction
  | getDetailtStatSuccessAction
  | requestPendingDetailsGraphAction
  | cleanerDetailStatAction
  | getSummaryAction
  | getDetailsAction
  | cleanerStatAction
  | requestPendingAction
  | requestPendingGraphAction
  | serverErrorsAction
  | getDetailsGraphSuccessAction
  | disposeDetailsAction
  | setSelectedTypeAction
  | getDetailsTableDataAction
  | getDetailsInfoAction
  | setActiveCounterAction
export const getStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_STAT_SUCCESS,
  defaultState
)
type getStatSuccessAction = ReturnType<typeof getStatSuccess>

export const getDetailtStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_DETAIL_STAT_SUCCESS,
  defaultState.details
)
type getDetailtStatSuccessAction = ReturnType<typeof getDetailtStatSuccess>

const getSummary = (
  totals: NetworkTotals,
  overviewList: NetworkSummaryItem[]
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_SUMMARY_NETWORK, {
    totals,
    overviewList,
    isPending: false
  })
type getSummaryAction = ReturnType<typeof getSummary>

const getDetails = (details: NetworkDetailInfo, info: InfoDetailsResult) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_NETWORK_DETAILS, {
    ...details,
    info,
    isPending: false
  })
type getDetailsAction = ReturnType<typeof getDetails>

const getDetailsInfo = (details: NetworkDetailInfo, info: InfoDetailsResult) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_NETWORK_DETAILS_INFO,
    {
      ...details,
      info,
      isPending: false
    }
  )
type getDetailsInfoAction = ReturnType<typeof getDetailsInfo>

const getDetailsTableData = (details: NetworkDetailInfo) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_NETWORK_DETAILS_TABLE_DATA,
    {
      ...details,
      isPending: false
    }
  )
type getDetailsTableDataAction = ReturnType<typeof getDetailsTableData>

export const setActiveCounter = (value: string) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.SET_NETWORK_DETAILS_ACTIVE_COUNTER,
    {
      selectedCounter: value
    }
  )
type setActiveCounterAction = ReturnType<typeof setActiveCounter>

interface GetNetworking {
  instanceInfoList: InstanceInfo[]
  sgInfoList: SGInfo[]
  subnetInfoList: SubnetInfo[]
  sgRulesInfoList: SGRulesInfo[]
  routesInfoList: RouteInfo[]
}
const getNetworking = ({
  instanceInfoList,
  sgInfoList,
  subnetInfoList,
  sgRulesInfoList,
  routesInfoList
}: GetNetworking) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_NETWORKING_SUCCESS, {
    instanceInfoList,
    sgInfoList,
    subnetInfoList,
    sgRulesInfoList,
    routesInfoList,
    isPending: false
  })
type getNetworkingAction = ReturnType<typeof getNetworking>

export const cleanerStat = ReduxHelpers.Common.Actions.cleanerState(
  ActionTypes.CLEANER_STAT,
  defaultState
)

type cleanerStatAction = ReturnType<typeof cleanerStat>

export const disposeDetails = () =>
  ReduxHelpers.Actions.createAction(ActionTypes.DISPOSE_DETAILS)
type disposeDetailsAction = ReturnType<typeof disposeDetails>

export const cleanerDetailStat = ReduxHelpers.Common.Actions.cleanerState(
  ActionTypes.CLEANER_DETAIL_STAT,
  defaultState.details
)

type cleanerDetailStatAction = ReturnType<typeof cleanerDetailStat>

const serverErrors = (errors: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SERVER_ERRORS, {
    errors,
    isPending: false
  })
type serverErrorsAction = ReturnType<typeof serverErrors>

const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export function getDetailsGraphSuccess(
  graph: Graph,
  table?: any[],
  isPending = false
) {
  return { type: ActionTypes.GET_DETAILS_GRAPH_SUCCESS, graph, isPending }
}

type getDetailsGraphSuccessAction = ReturnType<typeof getDetailsGraphSuccess>

export const requestPendingGraph = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING_GRAPH, {
    isPending
  })
type requestPendingGraphAction = ReturnType<typeof requestPendingGraph>

export const requestPendingDetailsGraph = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.REQUEST_PENDING_DETAIL_GRAPH,
    {
      isPending
    }
  )
type requestPendingDetailsGraphAction = ReturnType<
  typeof requestPendingDetailsGraph
>

export function cleaner() {
  return (dispatch: any, getState: () => State) => {
    dispatch(disposeDetails())
  }
}

export const setSelectedType = (type: ResourceTypeEnum) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_SELECTED_TYPE, {
    selectedNetwork: type
  })
type setSelectedTypeAction = ReturnType<typeof setSelectedType>

export function selectTile(tile: NetworkTiles, type: ResourceTypeEnum) {
  return (dispatch: any, getState: () => State) => {
    const { networks } = getState()
    const { details } = networks
    const tabs = getTabs(tile, type)
    dispatch(
      getStatSuccess('details', {
        ...details,
        selectedTile: tile,
        selectedTab: tabs[0]
      })
    )
  }
}

export function selectTab(tab: NetworkTabs, options: GetNetworkDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    dispatch(getDetailtStatSuccess('selectedTab', tab))
    if (tab === NetworkTabs.Total || tab === NetworkTabs.Used) {
      dispatch(setActiveCounter(CounterFromTab[tab]))
      return
    }

    dispatch(getNetworkTableData(options, tabsConfig[tab]))
  }
}

const parseNetworkItem = (
  item: NetworkSummaryInfo = {},
  accountInfo: AccountInfo[],
  resourceTypes: ResourceTypeDict
): NetworkSummaryItem => {
  const { tenantId, cloudType, accountId, region, type, id } =
    parseCamURI(item.uri) || {}
  return {
    ...item,
    tenantId,
    cloudType,
    accountId,
    object_type: getHumanNameByType(type, resourceTypes),
    account_name: getAccountNameById(accountId, accountInfo),
    region_name: region,
    type,
    id
  }
}

export function getSummaryNetwork(type?: string) {
  return (dispatch: any, getState: () => State) => {
    const { filter, auth, shell } = getState()
    const { tenant } = auth
    const { account_info = [] } = tenant || ({} as Tenant)
    const { resourceTypes = {} } = shell
    dispatch(requestPending(true))
    dispatch(requestPendingGraph(true))
    dispatch(setSelectedType(type as ResourceTypeEnum))
    GraphQueryMgr.NetworkQuery({ params: paramsWithFilter({ type }, filter) })
      .then(res => {
        const { network_count = {}, network_summary_info } = res
        const totals = getNetworkTotals(network_count)
        const data: NetworkSummaryItem[] = (network_summary_info || []).map(
          item => parseNetworkItem(item, account_info, resourceTypes)
        )
        dispatch(getSummary(totals, data))
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}

const parseNetworkingItems = <T extends { uri?: string }>(data: T[]) =>
  map(data, x => ({
    ...x,
    type: getTypeFromUri(x.uri),
    region_name: getRegionFromUri(x.uri)
  }))

interface GetNetworkDetailsOptions extends BaseOptions {
  callback?: (data?: any) => void
}

const parseInstanceRole = (item: InstanceRoleDetails) => {
  const total_actions = {
    READ: item?.total_read_count || 0,
    WRITE: item?.total_write_count || 0,
    ADMIN: item?.total_admin_count || 0
  }
  const used_actions = {
    READ: item?.used_read_count || 0,
    WRITE: item?.used_write_count || 0,
    ADMIN: item?.used_admin_count || 0
  }
  return {
    ...item,
    entity_type: getTypeFromUri(item.entity_id),
    total_actions,
    used_actions,
    actions_str: parseActionsFromObject(total_actions)
  }
}

export function getNetworkInfo({
  uri,
  type,
  accountId,
  callback
}: GetNetworkDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    GraphQueryMgr.NetworkServiceQuery({
      params: {
        data_type: DataType.INFO,
        uri,
        type,
        account_id: accountId
      }
    })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { network_detail_info = {} as NetworkDetailInfo } =
          res || ({} as NetworkQueryResp)
        const { valueMap = 'null' } = network_detail_info
        const infoDetails: ResourceInfo = getInfo(valueMap)
        if (type === ResourceTypeEnum.INSTANCE) {
          const parsedHumanNames: HumanNamesResponse[] =
            network_detail_info.sg_detail
              ? JSON.parse(network_detail_info.sg_detail)
              : []

          const humanNames = mapToDictionary({
            array: parsedHumanNames || [],
            key: 'URI',
            iterator: x => ({ name: x.Name, uri: x.URI })
          })

          const humanNamesResult: InfoDetailsResult = parseInfoDetails(
            infoDetails,
            humanNames
          )
          dispatch(getDetailsInfo(network_detail_info, humanNamesResult || {}))
        } else {
          dispatch(getDetailsInfo(network_detail_info, infoDetails))
        }
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}

function handleNetworkingTable(network_detail_info: NetworkDetailInfo) {
  return (dispatch: any, getState: () => State) => {
    const {
      net_instance_info = [],
      net_sg_info = [],
      net_subnet_info = [],
      sg_info,
      routes = []
    } = network_detail_info
    const sgRulesInfo = sg_info?.rules || []
    const instanceInfoList: InstanceInfo[] =
      parseNetworkingItems(net_instance_info)
    const sgInfoList: SGInfo[] = parseNetworkingItems(net_sg_info)
    const subnetInfoList: SubnetInfo[] = parseNetworkingItems(net_subnet_info)
    const sgRulesInfoList: SGRulesInfo[] = formatSGRulesList(sgRulesInfo)
    const routesInfoList: RoutesInfo[] = routes
    dispatch(
      getNetworking({
        instanceInfoList,
        sgInfoList,
        subnetInfoList,
        sgRulesInfoList,
        routesInfoList
      })
    )
  }
}

function handleIamTable(network_detail_info: NetworkDetailInfo) {
  return (dispatch: any, getState: () => State) => {
    dispatch(getDetailsTableData(network_detail_info))
  }
}

function handleRoleObjectsTable(network_detail_info: NetworkDetailInfo) {
  return (dispatch: any, getState: () => State) => {
    const { instance_role_details = [] } = network_detail_info
    const instanceRoleList: InstanceRole[] = map(
      instance_role_details || [],
      x => parseInstanceRole(x)
    )
    dispatch(getDetailtStatSuccess('instanceRoleList', instanceRoleList))
    dispatch(getDetailsTableData(network_detail_info))
  }
}

export function getNetworkTableData(
  { uri, type, accountId, callback }: GetNetworkDetailsOptions,
  { tableTab, tile }: NetworkTileConfiguration
) {
  return (dispatch: any, getState: () => State) => {
    const params = pickBy(
      {
        uri,
        type,
        tableTab,
        tile,
        account_id: accountId,
        data_type: DataType.TABLE_DATA
      },
      identity
    )

    dispatch(requestPending(true))
    GraphQueryMgr.NetworkServiceQuery({
      params
    })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { network_detail_info = {} as NetworkDetailInfo } =
          res || ({} as NetworkQueryResp)

        switch (tile) {
          case NetworkTileType.TILE_NETWORK_IAM:
            dispatch(handleIamTable(network_detail_info))
            break
          case NetworkTileType.TILE_NETWORK_NETWORKING:
            dispatch(handleNetworkingTable(network_detail_info))
            break
          case NetworkTileType.TILE_NETWORK_OBJECTS:
          case NetworkTileType.TILE_NETWORK_ROLES:
            dispatch(handleRoleObjectsTable(network_detail_info))
            break
          default:
            break
        }
        if (callback) {
          callback()
        }
        dispatch(requestPending(false))
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}

export function getInfoData(options: GetNetworkDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    const tileConfig = getConfigFromType(options.type)
    dispatch(getNetworkInfo(options))
    dispatch(getNetworkTableData(options, tileConfig))
    dispatch(getGraphData(options.uri, options.type, options.accountId))
  }
}

export function getNetworkingInfoData(
  options: GetNetworkDetailsOptions,
  {
    tableTab = NetworkTableTab.TABLE_TAB_IAM_USER,
    tile
  }: NetworkTileConfiguration
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(getNetworkTableData(options, { tableTab, tile }))
  }
}

export function getInstanceRoleData(
  options: GetNetworkDetailsOptions,
  tile: NetworkTiles
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(getNetworkTableData(options, NetworkTileConfiguration[tile]))
  }
}

// use for get Graph Top Level
export function getGraphData(
  uri: string,
  type: ResourceTypeEnum,
  account_id: string
) {
  return (dispatch: any, getState: () => State) => {
    const params: NetworkServiceQueryReq = {
      uri,
      type,
      account_id,
      data_type: DataType.ROLL_UP_GRAPH
    }
    const { shell } = getState()
    const { resourceTypes } = shell

    dispatch(requestPendingGraph(true))
    GraphQueryMgr.NetworkServiceQuery({ params })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { network_detail_info = {} as NetworkDetailInfo } = res
        const { roll_up_graph } = network_detail_info

        const graphParsed = mapDataToGraph({
          data: roll_up_graph,
          resourceTypes,
          options: {
            mainIdKey: 'identity_uri',
            mainNameKey: 'identity',
            typeKey: 'resource_type',
            countKey: 'resource_count'
          }
        })

        dispatch(getDetailsGraphSuccess(graphParsed))
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}
